import comms from '../../comms';
import { offlineStorage } from '../../../services/offlineStorage';
import { connectionManager } from '../../../services/connectionManager';

let isInitialized = false;

// Helper function to format minutes to time string
const formatMinutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  const today = new Date();
  today.setHours(hours, mins, 0, 0);
  
  return today.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
};

/**
 * Send delete log message with offline support
 * If offline, queues the action with original timestamp
 */
const sendDeleteLogMessage = (logId, messageType, additionalData = null) => {
  const timestamp = Date.now(); // Capture when action occurred
  
  if (additionalData && additionalData.itineraryId) {
    // New format with comprehensive data including itineraryId
    const deleteMessage = {
      type: `LOOPED_ROUTE_DELETE_${messageType}`,
      logId: logId,
      messageType: messageType,
      _originalTimestamp: timestamp, // When action actually occurred
      ...additionalData
    };
    const message = `LOOPED_ROUTE_DELETE_${messageType}|${JSON.stringify(deleteMessage)}`;
    
    // Check if online
    const socket = comms.getSocket();
    if (socket && socket.connected) {
      comms.sendSocket(message);
    } else {
      // Offline: queue for later
      offlineStorage.queueAction({
        type: `LOOPED_ROUTE_DELETE_${messageType}`,
        data: deleteMessage,
        messageFormat: message,
        timestamp: timestamp,
      });
    }
  } else {
    // Legacy format for backward compatibility
    const message = `DELETE_LOG|${logId}|${messageType}`;
    
    const socket = comms.getSocket();
    if (socket && socket.connected) {
      comms.sendSocket(message);
    } else {
      // Offline: queue for later
      offlineStorage.queueAction({
        type: 'DELETE_LOG',
        data: { logId, messageType, _originalTimestamp: timestamp },
        messageFormat: message,
        timestamp: timestamp,
      });
    }
  }
};

// Helper function to process looped schedule data specifically
const processLoopedScheduleData = (fixedRoute, vehicleId, passengerLogs = []) => {
  if (!fixedRoute) {
    return { 
      Id: null,
      daySchedule: [], 
      currentWaypoint: null, 
      loopIterations: [],
      currentLoopIteration: 0,
      currentLoopStops: [],
      averageLoopDuration: null,
      estimatedLoopCompletion: null,
      loopStartTime: null,
      startPointLog: null,
      breakLogs: [],
      endPointLog: null,
      canChangeStartPoint: true,
      canDeleteBreaks: false,
      canDeleteEndPoint: false,
      isOnBreak: false
    };
  }

  if (!fixedRoute.Stops || !fixedRoute.Runs || !fixedRoute.Schedules) {
    
    return { 
      Id: fixedRoute?.Id || null,
      daySchedule: [], 
      currentWaypoint: null, 
      loopIterations: [],
      currentLoopIteration: 0,
      currentLoopStops: [],
      averageLoopDuration: null,
      estimatedLoopCompletion: null,
      loopStartTime: null,
      startPointLog: null,
      breakLogs: [],
      endPointLog: null,
      canChangeStartPoint: true,
      canDeleteBreaks: false,
      canDeleteEndPoint: false,
      isOnBreak: false
    };
  }

  const now = new Date();
  const todayMinutes = now.getHours() * 60 + now.getMinutes();

  // Find vehicle's active schedules for today
  const dayOfWeek = now.getDay();
  const dayNames = ['ActiveSunday', 'ActiveMonday', 'ActiveTuesday', 'ActiveWednesday', 'ActiveThursday', 'ActiveFriday', 'ActiveSaturday'];
  const todayProperty = dayNames[dayOfWeek];

  let activeRunSchedule = null;
  
  // Find the looped run schedule
  if (fixedRoute.Schedules && Array.isArray(fixedRoute.Schedules)) {
    for (const schedule of fixedRoute.Schedules) {
      const isForThisVehicle = !schedule.VehicleId || schedule.VehicleId === vehicleId;
      const isActiveToday = schedule[todayProperty];
      
      if (isForThisVehicle && isActiveToday && schedule.Runschedule && Array.isArray(schedule.Runschedule)) {
        // Check if this is a looping schedule
        const runSchedule = schedule.Runschedule[0];
        if (runSchedule) {
          const scheduleDuration = runSchedule.EndTime - runSchedule.StartTime;
          const run = fixedRoute.Runs.find(r => r.Id === runSchedule.RunId);
          
          if (run && run.Itinerary) {
            // Calculate total loop time by summing all ScheduledETA values
            const totalLoopTime = run.Itinerary.reduce((sum, item) => sum + (item.ScheduledETA || 0), 0);
            const isLoopingSchedule = schedule.Runschedule.length === 1 && scheduleDuration > totalLoopTime;
            
            if (isLoopingSchedule) {
              activeRunSchedule = {
                ...runSchedule,
                schedule: schedule,
                run: run,
                scheduleDuration: scheduleDuration,
                totalLoopTime: totalLoopTime
              };
              break;
            }
          }
        }
      }
    }
  }

  if (!activeRunSchedule) {
    return { 
      Id: fixedRoute.Id,
      daySchedule: [], 
      currentWaypoint: null, 
      loopIterations: [],
      currentLoopIteration: 0,
      currentLoopStops: [],
      averageLoopDuration: null,
      estimatedLoopCompletion: null,
      loopStartTime: null,
      startPointLog: null,
      breakLogs: [],
      endPointLog: null,
      canChangeStartPoint: true,
      canDeleteBreaks: false,
      canDeleteEndPoint: false,
      isOnBreak: false
    };
  }

  // Process passenger logs to categorize by PAXBoarded values
  const runLogs = (fixedRoute.Logs || [])
    .filter(log => log.RunName === activeRunSchedule.run.Name)
    .sort((a, b) => new Date(a.CreatedOn) - new Date(b.CreatedOn));

  // Categorize logs by PAXBoarded values
  const startPointLogs = runLogs.filter(log => log.PAXBoarded === -1);
  const breakLogs = runLogs.filter(log => log.PAXBoarded === -2);
  const endPointLogs = runLogs.filter(log => log.PAXBoarded === -3);
  const actualStopLogs = runLogs.filter(log => log.PAXBoarded >= 0); // 0 or positive are actual stops
  
  // Get the current start point (most recent -1 log)
  const startPointLog = startPointLogs.length > 0 ? startPointLogs[startPointLogs.length - 1] : null;
  
  // Get the current end point (most recent -3 log)
  const endPointLog = endPointLogs.length > 0 ? endPointLogs[endPointLogs.length - 1] : null;
  
  // Filter active breaks (those that haven't been departed from yet)
  const activeBreakLogs = breakLogs.filter(breakLog => {
    // Check if driver has already departed from this break stop after the break was scheduled
    const departureAfterBreak = actualStopLogs.find(log => 
      log.StopId === breakLog.StopId && 
      new Date(log.CreatedOn) > new Date(breakLog.CreatedOn)
    );
    
    return !departureAfterBreak; // Active if no departure after break was scheduled
  });

  // Find the last actual stop performed (highest PAXBoarded >= 0)
  const lastActualStopLog = actualStopLogs.length > 0 ? actualStopLogs[actualStopLogs.length - 1] : null;
  
  // Determine operational capabilities and current state
  const hasActualStops = actualStopLogs.length > 0;
  const canChangeStartPoint = !hasActualStops || !startPointLog; // Can change if no actual stops OR no start point set
  
  // Check if driver is currently on break
  let isOnBreak = false;
  let currentBreakLog = null;
  let breakTimeRemaining = 0;
  
  if (lastActualStopLog) {
    // Check if the last stop was a break stop and we haven't departed yet
    const breakAtLastStop = activeBreakLogs.find(log => log.StopId === lastActualStopLog.StopId);
    if (breakAtLastStop) {
      const breakDepartureTime = new Date(breakAtLastStop.UpdatedOn);
      const currentTime = new Date();
      const arrivalTime = new Date(lastActualStopLog.CreatedOn);
      
      // Driver is on break if they arrived at break stop and it's not yet time to depart
      if (currentTime < breakDepartureTime) {
        isOnBreak = true;
        currentBreakLog = breakAtLastStop;
        breakTimeRemaining = Math.ceil((breakDepartureTime.getTime() - currentTime.getTime()) / (1000 * 60));
      }
    }
  }
  
  // Can delete breaks if they haven't been reached yet or if currently on break
  const canDeleteBreaks = activeBreakLogs.length > 0;
  
  // Can delete end point if it exists and hasn't been reached
  const canDeleteEndPoint = endPointLog !== null && (!lastActualStopLog || lastActualStopLog.StopId !== endPointLog.StopId);

  // Check if route is completed (reached end point)
  if (endPointLog && lastActualStopLog && lastActualStopLog.StopId === endPointLog.StopId) {
    console.log('Route completion reached - end point has been visited');
    const endStop = fixedRoute.Stops.find(s => s.Id === endPointLog.StopId);
    
    return {
      Id: fixedRoute.Id,
      daySchedule: [],
      currentWaypoint: null,
      loopIterations: [],
      currentLoopIteration: 0,
      currentLoopStops: [],
      averageLoopDuration: null,
      estimatedLoopCompletion: null,
      loopStartTime: null,
      scheduleStatus: 'route_completed',
      scheduleStartTime: formatMinutesToTime(activeRunSchedule.StartTime),
      scheduleEndTime: formatMinutesToTime(activeRunSchedule.EndTime),
      nextStops: [],
      availableStops: [],
      activeRunSchedule: activeRunSchedule,
      needsInitialStopSelection: false,
      isRouteCompleted: true,
      routeCompletionInfo: {
        completionStop: endStop,
        completionTime: new Date(lastActualStopLog.CreatedOn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
        routeName: fixedRoute.Name,
        totalStopsCompleted: actualStopLogs.length,
        completedAt: lastActualStopLog.CreatedOn
      },
      startPointLog: startPointLog,
      breakLogs: activeBreakLogs,
      endPointLog: endPointLog,
      canChangeStartPoint: false,
      canDeleteBreaks: false,
      canDeleteEndPoint: false,
      isOnBreak: false,
      breakTimeRemaining: 0
    };
  }

  // If there are no logs at all OR no meaningful logs (no start point and no actual stops), show initial stop selection
  if (runLogs.length === 0 || (!startPointLog && !lastActualStopLog)) {

    
    // For route selection, create an initial schedule starting from the first stop
    const now = new Date();
    const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
    
    const driverFocusedStops = [];
    const totalStops = activeRunSchedule.run.Itinerary.length;
    
    // Build all stops in the route as upcoming stops
    for (let i = 0; i < totalStops; i++) {
      const itineraryItem = activeRunSchedule.run.Itinerary[i];
      const stop = fixedRoute.Stops.find(s => s.Id === itineraryItem.StopId);
      
      if (!stop) continue;
      
      // Calculate estimated arrival time based on schedule
      const scheduledMinutes = activeRunSchedule.StartTime + (itineraryItem.ScheduledETA || 0);
      const estimatedArrivalTime = formatMinutesToTime(scheduledMinutes);
      
      const stopData = {
        ...stop,
        stopNumber: i + 1,
        etaMinutes: itineraryItem.ScheduledETA || 0,
        estimatedArrivalTime: estimatedArrivalTime,
        isCurrent: i === 0,
        stopIndex: i,
        scheduledTime: estimatedArrivalTime,
        hasScheduledBreak: false,
        breakDepartureTime: null,
        breakLogId: null,
        isEndPoint: false,
        isOnBreak: false,
        breakTimeRemaining: 0
      };
      
      driverFocusedStops.push(stopData);
    }
    
    // Build daySchedule from the stops
    const daySchedule = driverFocusedStops.map((stop, index) => ({
      stop: stop,
      itineraryItem: activeRunSchedule.run.Itinerary.find(item => item.StopId === stop.Id),
      run: activeRunSchedule.run,
      schedule: activeRunSchedule.schedule,
      runSchedule: activeRunSchedule,
      address: stop.Name,
      arrival: stop.estimatedArrivalTime,
      scheduledTime: stop.scheduledTime,
      estimatedArrivalTime: stop.estimatedArrivalTime,
      etaMinutes: stop.etaMinutes,
      completed: false,
      isLoopingStop: true,
      loopIndex: 0,
      stopIndex: index,
      stopNumber: stop.stopNumber,
      isCurrent: stop.isCurrent,
      isNext: index === 0,
      stopId: stop.Id,
      coordinates: { lat: stop.Lat, lng: stop.Lon },
      key: `${stop.Id}_initial_${index}`,
      hasScheduledBreak: stop.hasScheduledBreak,
      breakDepartureTime: stop.breakDepartureTime,
      breakLogId: stop.breakLogId,
      isEndPoint: stop.isEndPoint,
      isOnBreak: stop.isOnBreak,
      breakTimeRemaining: stop.breakTimeRemaining
    }));
    
    const allStops = driverFocusedStops.map((stop, index) => ({
      ...stop,
      stopIndex: index,
      scheduledTime: stop.scheduledTime
    }));

    return {
      Id: fixedRoute.Id,
      daySchedule: daySchedule, // Return the built schedule instead of empty array
      currentWaypoint: driverFocusedStops[0] || null,
      loopIterations: [],
      currentLoopIteration: 0,
      currentLoopStops: driverFocusedStops,
      averageLoopDuration: null,
      estimatedLoopCompletion: null,
      loopStartTime: null,
      scheduleStatus: 'awaiting_initial_stop',
      scheduleStartTime: formatMinutesToTime(activeRunSchedule.StartTime),
      scheduleEndTime: formatMinutesToTime(activeRunSchedule.EndTime),
      nextStops: driverFocusedStops.slice(0, 5),
      availableStops: allStops,
      activeRunSchedule: activeRunSchedule,
      needsInitialStopSelection: true,
      startPointLog: null,
      breakLogs: [],
      endPointLog: null,
      canChangeStartPoint: true,
      canDeleteBreaks: false,
      canDeleteEndPoint: false,
      isOnBreak: false,
      breakTimeRemaining: 0
    };
  }

  // Build route based on current state
  return buildRouteFromCurrentState(
    fixedRoute, 
    activeRunSchedule, 
    lastActualStopLog, 
    startPointLog, 
    activeBreakLogs, 
    endPointLog, 
    isOnBreak, 
    currentBreakLog,
    breakTimeRemaining,
    canChangeStartPoint, 
    canDeleteBreaks, 
    canDeleteEndPoint
  );
};

// Build route based on current state (either from last actual stop or start point)
const buildRouteFromCurrentState = (
  fixedRoute, 
  activeRunSchedule, 
  lastActualStopLog, 
  startPointLog, 
  breakLogs, 
  endPointLog, 
  isOnBreak, 
  currentBreakLog,
  breakTimeRemaining,
  canChangeStartPoint, 
  canDeleteBreaks, 
  canDeleteEndPoint
) => {
  let baseTime, currentStopIndex, loopIteration = 0;
  
  // Determine starting point and time
  if (lastActualStopLog) {
    // We have actual stops - start from the last one
    const lastStopIndex = activeRunSchedule.run.Itinerary.findIndex(item => item.StopId === lastActualStopLog.StopId);
    if (lastStopIndex === -1) {
      console.error('Last stop not found in itinerary');
      return getErrorResponse(fixedRoute.Id);
    }
    
    // If on break, use break departure time as base time, otherwise use stop departure time
    if (isOnBreak && currentBreakLog) {
      baseTime = new Date(currentBreakLog.UpdatedOn); // Break departure time
      currentStopIndex = lastStopIndex; // Stay at current stop until break ends
    } else {
      baseTime = new Date(lastActualStopLog.UpdatedOn || lastActualStopLog.CreatedOn);
      currentStopIndex = (lastStopIndex + 1) % activeRunSchedule.run.Itinerary.length;
      
      // Calculate which loop iteration we're in
      loopIteration = Math.floor((lastStopIndex + 1) / activeRunSchedule.run.Itinerary.length);
    }
  } else if (startPointLog) {
    // No actual stops but we have a start point
    const startStopIndex = activeRunSchedule.run.Itinerary.findIndex(item => item.StopId === startPointLog.StopId);
    
    if (startStopIndex === -1) {
      console.error('Start point not found in itinerary');
      return getErrorResponse(fixedRoute.Id);
    }
    
    // For start point, the UpdatedOn time represents when they want to be AT that stop
    // The selected start point is the current waypoint (where they're traveling TO)
    // The itinerary should start with the NEXT stop after the selected start point
    baseTime = new Date(startPointLog.UpdatedOn); // Time when driver will be AT the start point
    currentStopIndex = (startStopIndex + 1) % activeRunSchedule.run.Itinerary.length; // Start building from NEXT stop
    loopIteration = 0;
  } else {
    console.error('No starting point available - this is likely why daySchedule is empty');
    console.error('Debug info:', {
      lastActualStopLog: !!lastActualStopLog,
      startPointLog: !!startPointLog
    });
    return getErrorResponse(fixedRoute.Id);
  }

  const driverFocusedStops = [];
  const totalStops = activeRunSchedule.run.Itinerary.length;
  
  // Build upcoming stops until we reach the end point (if any)
  let stopsBuilt = 0;
  const maxStopsToShow = endPointLog ? totalStops : totalStops; // Show all stops unless we have an end point

  for (let i = 0; i < maxStopsToShow && stopsBuilt < totalStops; i++) {
    const stopIndex = (currentStopIndex + i) % totalStops;
    const itineraryItem = activeRunSchedule.run.Itinerary[stopIndex];
    const stop = fixedRoute.Stops.find(s => s.Id === itineraryItem.StopId);
    let cumulativeTime = 0;
    
    if (!stop) continue;

    // If this is the end point, stop building here (but include this stop)
    const isEndPoint = endPointLog && stop.Id === endPointLog.StopId;
    
    // Calculate ETA using scheduled times
  
    let effectiveBaseTime = baseTime;
    
    if (i === 0 && isOnBreak) {
      // If on break at current stop, arrival time is now but can't leave until break ends
      cumulativeTime = 0;
      effectiveBaseTime = new Date(); // Current time for arrival
    } else if (i === 0 && startPointLog) {
      // First stop from start point - this IS the start point, use the selected time directly
      cumulativeTime = 0;
      effectiveBaseTime = baseTime; // Use the start point time directly
    } else {
      // Calculate cumulative travel time from base point
      if (i === 0) {
        
        // First stop - use the ScheduledETA of the PREVIOUS stop (where we're traveling FROM)
        // The ScheduledETA tells us how long it takes to get FROM that stop TO the next stop
        const previousStopIndex = (currentStopIndex - 1 + totalStops) % totalStops;
        const previousItineraryItem = activeRunSchedule.run.Itinerary[previousStopIndex];

        cumulativeTime = previousItineraryItem.ScheduledETA || 0;
      } else {
        // Subsequent stops - start with travel time to first stop, then add subsequent segments
        const firstStopIndex = (currentStopIndex +  totalStops ) % totalStops;
        const firstItineraryItem = activeRunSchedule.run.Itinerary[firstStopIndex];
     //   cumulativeTime =  cumulativeTime + firstItineraryItem.ScheduledETA || 0;

        // Add travel times for subsequent segments
        for (let j = 0; j <= i; j++) {
          const segmentIndex = (currentStopIndex + j ) % totalStops;
          const segmentItem = activeRunSchedule.run.Itinerary[segmentIndex];
          const segmentTime = segmentItem.ScheduledETA || 0;
        }
      }
      
      // Calculate arrival time at target stop, accounting for breaks along the route
      let currentTime = effectiveBaseTime.getTime();
      let totalTravelTime = 0;
      
      if (i === 0) {
        // First stop - just add travel time to this stop
        totalTravelTime = cumulativeTime;
        const targetStopId = itineraryItem.StopId;
        const breakAtTarget = breakLogs.find(log => log.StopId === targetStopId);
        
        if (breakAtTarget) {
          const normalArrival = new Date(currentTime + (totalTravelTime * 60 * 1000));
          const breakDeparture = new Date(breakAtTarget.UpdatedOn);
          
          // If break departure is later than normal arrival, use break departure time
          if (breakDeparture.getTime() > normalArrival.getTime()) {
            const finalArrival = breakDeparture.getTime();
            totalTravelTime = (finalArrival - currentTime) / (1000 * 60);
          }
        }
        
        cumulativeTime = totalTravelTime;
      } else {
        // Multi-stop calculation - check each segment for breaks
        let segmentStartTime = currentTime;
        totalTravelTime = 0;
        
        // Add travel time to first stop - use ScheduledETA from the PREVIOUS stop
        const previousStopIndex = (currentStopIndex - 1 + totalStops) % totalStops;
        const previousItineraryItem = activeRunSchedule.run.Itinerary[previousStopIndex];
        let segmentTime = previousItineraryItem.ScheduledETA || 0;
        totalTravelTime += segmentTime;
        
        // Check if first stop has a break
        const firstStopId = activeRunSchedule.run.Itinerary[currentStopIndex].StopId;
        const breakAtFirst = breakLogs.find(log => log.StopId === firstStopId);
        if (breakAtFirst) {
          const normalArrival = new Date(segmentStartTime + (totalTravelTime * 60 * 1000));
          const breakDeparture = new Date(breakAtFirst.UpdatedOn);
          
          if (breakDeparture.getTime() > normalArrival.getTime()) {
            const delay = (breakDeparture.getTime() - normalArrival.getTime()) / (1000 * 60);
            totalTravelTime += delay;
          }
        }
        
        // Add subsequent segments
        let hasIntermediateBreak = false;
        let breakDepartureTime = null;
        let breakAtStopIndex = -1;
        
        for (let j = 1; j <= i; j++) {
          // segmentIndex represents the stop we're traveling FROM
          const segmentIndex = (currentStopIndex + j - 1) % totalStops;
          const segmentItem = activeRunSchedule.run.Itinerary[segmentIndex];
          // ScheduledETA tells us how long it takes to get FROM this stop TO the next stop
          segmentTime = segmentItem.ScheduledETA || 0;
          totalTravelTime += segmentTime;
          
          // Check if the DESTINATION stop (where we're going TO) has a break
          const destinationIndex = (currentStopIndex + j) % totalStops;
          const destinationItem = activeRunSchedule.run.Itinerary[destinationIndex];
          const destinationStopId = destinationItem.StopId;
          const breakAtDestination = breakLogs.find(log => log.StopId === destinationStopId);
          
          if (breakAtDestination) {
            const normalArrival = new Date(segmentStartTime + (totalTravelTime * 60 * 1000));
            const breakDeparture = new Date(breakAtDestination.UpdatedOn);
            
            if (breakDeparture.getTime() > normalArrival.getTime()) {
              if (j < i) {
                // This is an intermediate stop with a break - track it for recalculation
                hasIntermediateBreak = true;
                breakDepartureTime = breakDeparture.getTime();
                breakAtStopIndex = j;
                // Don't modify totalTravelTime here - we'll recalculate from break point
              } else {
                // This is our target stop and it has a break - but ETA should show normal arrival
                // The break departure will be shown separately in the UI
                // Keep normal arrival time, don't extend to break departure
              }
            }
          }
        }
        
        // If we had an intermediate break, recalculate from that point
        if (hasIntermediateBreak && breakDepartureTime) {
          // Recalculate totalTravelTime from break departure to target
          const remainingSegments = i - breakAtStopIndex;
          let travelTimeFromBreak = 0;
          
          for (let k = 0; k < remainingSegments; k++) {
            const fromStopIndex = (currentStopIndex + breakAtStopIndex + k) % totalStops;
            const fromStopItem = activeRunSchedule.run.Itinerary[fromStopIndex];
            travelTimeFromBreak += fromStopItem.ScheduledETA || 0;
          }
          
          // Total time = time from start to break departure + travel time from break to target
          const timeToBreakDeparture = (breakDepartureTime - segmentStartTime) / (1000 * 60);
          totalTravelTime = timeToBreakDeparture + travelTimeFromBreak;
        }
        
        cumulativeTime = totalTravelTime;
      }
    }

    const estimatedArrival = new Date(effectiveBaseTime.getTime() + (cumulativeTime * 60 * 1000));
    const estimatedArrivalTime = estimatedArrival.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});

    // Check if this stop has a break
    const breakAtStop = breakLogs.find(log => log.StopId === stop.Id);
    
    // Determine if this is the current waypoint (where they are traveling TO)
    // If we have a start point log, the selected start point is the current waypoint
    // The itinerary is reordered to start AFTER the selected start point, so we need to check
    // if this stop matches the start point stop ID, regardless of its position in the reordered list
    const isCurrentWaypoint = startPointLog ? (stop.Id === startPointLog.StopId) : (i === 0);
    
    const stopData = {
      ...stop,
      stopNumber: i ,
      etaMinutes: cumulativeTime,
      estimatedArrivalTime: estimatedArrivalTime,
      isCurrent: isCurrentWaypoint,
      stopIndex: stopIndex,
      scheduledTime: formatMinutesToTime(activeRunSchedule.StartTime + (itineraryItem.ScheduledETA || 0)),
      hasScheduledBreak: !!breakAtStop,
      breakDepartureTime: breakAtStop ? new Date(breakAtStop.UpdatedOn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : null,
      breakLogId: breakAtStop ? breakAtStop.Id : null,
      isEndPoint: isEndPoint,
      isOnBreak: i === 0 && isOnBreak,
      breakTimeRemaining: i === 0 && isOnBreak ? breakTimeRemaining : 0
    };

    driverFocusedStops.push(stopData);
    stopsBuilt++;
    
    // If this was the end point, stop here
    if (isEndPoint) {
      break;
    }
  }

  // Find the current waypoint (the stop they are traveling TO)
  let currentWaypoint;
  
  if (startPointLog) {
    // If we have a start point log, the selected start point is the current waypoint
    const selectedStartStop = fixedRoute.Stops.find(s => s.Id === startPointLog.StopId);
    if (selectedStartStop) {
      currentWaypoint = {
        ...selectedStartStop,
        isCurrent: true,
        estimatedArrivalTime: new Date(startPointLog.UpdatedOn).toISOString(),
        etaMinutes: 0, // They will be there at the selected time
        stopNumber: -1, // Mark as special - this is the current waypoint, not in the itinerary
        isCurrentWaypoint: true
      };
    } else {
      currentWaypoint = driverFocusedStops[0];
    }
  } else {
    currentWaypoint = driverFocusedStops.find(stop => stop.isCurrent) || driverFocusedStops[0];
  }
  
  // Build day schedule
  const daySchedule = driverFocusedStops.map((stop, index) => ({
    stop: stop,
    itineraryItem: activeRunSchedule.run.Itinerary.find(item => item.StopId === stop.Id),
    run: activeRunSchedule.run,
    schedule: activeRunSchedule.schedule,
    runSchedule: activeRunSchedule,
    address: stop.Name,
    arrival: stop.estimatedArrivalTime,
    scheduledTime: stop.scheduledTime,
    estimatedArrivalTime: stop.estimatedArrivalTime,
    etaMinutes: stop.etaMinutes,
    completed: false,
    isLoopingStop: true,
    loopIndex: loopIteration,
    stopIndex: index,
    stopNumber: stop.stopNumber,
    isCurrent: stop.isCurrent,
    isNext: index === 0,
    stopId: stop.Id,
    coordinates: { lat: stop.Lat, lng: stop.Lon },
    key: `${stop.Id}_current_${index}`,
    hasScheduledBreak: stop.hasScheduledBreak,
    breakDepartureTime: stop.breakDepartureTime,
    breakLogId: stop.breakLogId,
    isEndPoint: stop.isEndPoint,
    isOnBreak: stop.isOnBreak,
    breakTimeRemaining: stop.breakTimeRemaining
  }));

  return {
    Id: fixedRoute.Id,
    daySchedule: daySchedule,
    currentWaypoint: currentWaypoint,
    loopIterations: [],
    currentLoopIteration: loopIteration,
    currentLoopStops: driverFocusedStops,
    averageLoopDuration: null,
    estimatedLoopCompletion: null,
    loopStartTime: startPointLog ? new Date(startPointLog.CreatedOn) : (lastActualStopLog ? new Date(lastActualStopLog.CreatedOn) : null),
    scheduleStatus: isOnBreak ? 'on_break' : 'active',
    scheduleStartTime: formatMinutesToTime(activeRunSchedule.StartTime),
    scheduleEndTime: formatMinutesToTime(activeRunSchedule.EndTime),
    nextStops: driverFocusedStops.slice(0, 5),
    availableStops: [],
    activeRunSchedule: activeRunSchedule,
    needsInitialStopSelection: false,
    startPointLog: startPointLog,
    breakLogs: breakLogs,
    endPointLog: endPointLog,
    canChangeStartPoint: canChangeStartPoint,
    canDeleteBreaks: canDeleteBreaks,
    canDeleteEndPoint: canDeleteEndPoint,
    isOnBreak: isOnBreak,
    breakTimeRemaining: breakTimeRemaining
  };
};

// Helper function to return error response
const getErrorResponse = (routeId) => {
  return {
    Id: routeId,
    daySchedule: [],
    currentWaypoint: null,
    loopIterations: [],
    currentLoopIteration: 0,
    currentLoopStops: [],
    averageLoopDuration: null,
    estimatedLoopCompletion: null,
    loopStartTime: null,
    scheduleStatus: 'error',
    scheduleStartTime: null,
    scheduleEndTime: null,
    nextStops: [],
    availableStops: [],
    activeRunSchedule: null,
    needsInitialStopSelection: false,
    startPointLog: null,
    breakLogs: [],
    endPointLog: null,
    canChangeStartPoint: false,
    canDeleteBreaks: false,
    canDeleteEndPoint: false,
    isOnBreak: false
  };
};

// Process job data for looped schedules
const processLoopedJobData = (daySchedule, currentWaypoint) => {
  if (!daySchedule || daySchedule.length === 0) {
    return [];
  }

  const jobs = [];
  
  if (currentWaypoint) {
    // Add current waypoint as first job
    jobs.push({
      ...currentWaypoint,
      location: [currentWaypoint.stop.Lat, currentWaypoint.stop.Lon],
      address: currentWaypoint.stop.Name,
      arrival: currentWaypoint.scheduledTime,
      instructions: `Stop ${currentWaypoint.stopIndex + 1} of Loop ${currentWaypoint.loopIndex + 1}`,
      phone: currentWaypoint.stop.Phone || '',
      completed: false,
      isLoopingStop: true,
      loopIndex: currentWaypoint.loopIndex
    });

    // Add remaining stops in current loop
    const remainingStops = daySchedule.filter(item => 
      !item.isVisited && item.key !== currentWaypoint.key
    );

    remainingStops.forEach(item => {
      jobs.push({
        ...item,
        location: [item.stop.Lat, item.stop.Lon],
        address: item.stop.Name,
        arrival: item.scheduledTime,
        instructions: `Stop ${item.stopIndex + 1} of Loop ${item.loopIndex + 1}`,
        phone: item.stop.Phone || '',
        completed: false,
        isLoopingStop: true,
        loopIndex: item.loopIndex
      });
    });
  }

  return jobs;
};

// Initialize socket for looped schedules
const initializeLoopedSocket = async (socket, setJobDetails, setDriverDetails, userSettings, fleetSettings, handleLoopedRouteData) => {
  // If no socket was passed, create one
  if (!socket) {
    socket = await comms.connectSocket(fleetSettings.dispatchUri);
  }
  if (isInitialized) return null;
  if (!socket) return null;

  let currentDaySchedule = [];
  let currentWaypointKey = null;
  let loopedRouteDataRef = null;

  // Send initial command
//  comms.sendSocket('START');

  socket.on('Command', function (msg) {
    console.log('Looped socket received message:', msg);
    
    try {
      // Handle empty or malformed messages
      if (!msg || typeof msg !== 'string') {
        console.warn('Received invalid message:', msg);
        return;
      }

      const messageParts = msg.split("|");
      
      // Acknowledge message if it has an ID
      if (messageParts[0]) {
        comms.sendSocket("ACK|" + messageParts[0]);
      }

      // Handle PING message - respond with current GPS
      if (messageParts[1] === 'PING') {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              comms.sendGPS(latitude, longitude, comms.mpsToMph(position.coords.speed));
            },
            (error) => {
              console.error('Error getting location for PING response:', error);
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 }
          );
        }
        return;
      }

      // Handle START messages or messages without proper JSON
      if (messageParts[1] === 'START' || !messageParts[2]) {
        return;
      }

      let msgObj;
      try {
        msgObj = JSON.parse(messageParts[2]);
      } catch (parseError) {
        console.warn('Failed to parse message JSON:', messageParts[2], parseError);
        return;
      }

      const result = {
        messageID: messageParts[0],
        command: messageParts[1],
        parameter: msgObj
      };

      if (result.command === 'DRIVER') {
        console.log('Driver Info for looped schedule', result.parameter);
        setDriverDetails(result.parameter);
        
        // Handle looped route data if present
        if (result.parameter && result.parameter.fixedRoute) {
          loopedRouteDataRef = result.parameter.fixedRoute;
          
          // Add passenger logs if available
          if (result.parameter.Logs && Array.isArray(result.parameter.Logs)) {
            loopedRouteDataRef.passengerLog = result.parameter.Logs;
          }
          
          // Get vehicle ID
          const vehicleId = result.parameter.vehicleId || 
                          result.parameter.Vehicle?.Id || 
                          (result.parameter.fixedRoute?.Vehicles && result.parameter.fixedRoute.Vehicles.length > 0 ? 
                           result.parameter.fixedRoute.Vehicles[0].Id : null);
          
          // Process looped schedule data
          const {
            daySchedule,
            currentWaypoint,
            loopIterations,
            currentLoopIteration,
            currentLoopStops,
            averageLoopDuration,
            estimatedLoopCompletion,
            loopStartTime,
            scheduleStatus,
            scheduleStartTime,
            scheduleEndTime
          } = processLoopedScheduleData(
            result.parameter.fixedRoute, 
            vehicleId,
            result.parameter.Logs || []
          );
          
          currentDaySchedule = daySchedule;
          
          // Pass enhanced looped route data to callback
          if (handleLoopedRouteData && typeof handleLoopedRouteData === 'function') {
            handleLoopedRouteData({
              ...result.parameter.fixedRoute,
              loopIterations,
              currentLoopIteration,
              currentLoopStops,
              averageLoopDuration,
              estimatedLoopCompletion,
              loopStartTime,
              scheduleStatus,
              scheduleStartTime,
              scheduleEndTime
            });
          }
          
          if (currentWaypoint) {
            currentWaypointKey = currentWaypoint.key;
            
            // Process jobs for display
            const jobs = processLoopedJobData(daySchedule, currentWaypoint);
            setJobDetails(jobs);
          }
        }
      }
      else if (result.command === 'DELETE_START_LOG_CONFIRMED') {
        // Request updated data to refresh the display

      }
      else if (result.command === 'DELETE_BREAK_LOG_CONFIRMED' || result.command === 'LOOPED_ROUTE_DELETE_BREAK_CONFIRMED') {
        // Trigger route update to refresh the display
        if (handleLoopedRouteData && result.parameter) {
          const data = JSON.parse(result.parameter);
          handleLoopedRouteData({
            action: 'LOOPED_ROUTE_DELETE_BREAK',
            logId: data.logId,
            stopId: data.stopId,
            success: data.success
          });
        }
      }
      else if (result.command === 'DELETE_END_LOG_CONFIRMED' || result.command === 'LOOPED_ROUTE_DELETE_END_CONFIRMED') {
        // Trigger route update to refresh the display
        if (handleLoopedRouteData && result.parameter) {
          const data = JSON.parse(result.parameter);
          handleLoopedRouteData({
            action: 'LOOPED_ROUTE_DELETE_END',
            logId: data.logId,
            stopId: data.stopId,
            success: data.success
          });
        }
      }
      else if (result.command === 'LOOPED_ROUTE_BEGIN_CONFIRMED') {
        // Request updated data to refresh the display

      }
      else if (result.command === 'ROUTE_END_CONFIRMED') {
        // Trigger route update to refresh the display
        if (handleLoopedRouteData && result.parameter) {
          const data = JSON.parse(result.parameter);
          handleLoopedRouteData({
            action: 'LOOPED_ROUTE_END',
            stopId: data.stopId,
            stopName: data.stopName,
            completedAt: data.completedAt,
            success: data.success
          });
        }
      }
      else if (result.command === 'FIXEDSTOP_ARRIVE' || result.command === 'FIXEDSTOP_DEPART') {
        // Handle arrivals and departures for looped routes
        
        if (!currentDaySchedule || currentDaySchedule.length === 0) {
          return;
        }

        // Parse the data for fixed route commands
        // Format: FIXEDSTOP_ARRIVE|{JSON data}
        // Format: FIXEDSTOP_DEPART|{JSON data}
        let messageData;
        try {
          messageData = JSON.parse(messageParts[1]);
        } catch (error) {
          console.error('Error parsing FIXEDSTOP message JSON:', error);
          return;
        }
        
        const rawStopId = messageData.stopId;
        const actualStopId = rawStopId.replace('SR-', ''); // Remove SR- prefix if present (backward compatibility)
        
        const data = {
          stopId: actualStopId,
          rawStopId: rawStopId,
          passengerCount: messageData.passengerCount || 0,
          runName: messageData.runName || '',
          runId: messageData.runId || '',
          routeId: messageData.routeId || '',
          scheduleId: messageData.scheduleId || '',
          driverId: messageData.driverId || '',
          vehicleId: messageData.vehicleId || '',
          timestamp: new Date().toISOString(),
          command: result.command
        };
        
        console.log(`Processing ${result.command} for stop ${data.stopId}`);
                   
        // Validate that we have the necessary run information
        if (!data.runId || !data.runName) {
          console.warn(`Missing run information in ${result.command} message:`, {
            runId: data.runId,
            runName: data.runName,
            messageData: messageData
          });
        }
        
        // Find the waypoint that matches this arrival/departure by stopId
        const matchingWaypoint = currentDaySchedule.find(item => 
          item.stop?.Id === data.stopId ||
          item.stop?.Id === data.rawStopId
        );
        
        if (!matchingWaypoint) {
          return;
        }

        // Handle arrivals and departures with proper waypoint advancement
        if (result.command === 'FIXEDSTOP_ARRIVE') {
          // For arrivals, just log - no waypoint advancement needed
        } else if (result.command === 'FIXEDSTOP_DEPART') {
          
          // Mark the current waypoint as visited
          const updatedSchedule = currentDaySchedule.map(item => {
            if (item.stop?.Id === data.stopId || item.stop?.Id === data.rawStopId) {
              // Update the stop data
              const updatedStop = {
                ...item.stop,
                PAXBoarded: data.passengerCount || 0,
                LastUpdated: data.timestamp
              };
              
              // Also update the original loopedRouteDataRef so future schedule builds use correct data
              if (loopedRouteDataRef && loopedRouteDataRef.Stops) {
                const originalStop = loopedRouteDataRef.Stops.find(s => s.Id === updatedStop.Id);
                if (originalStop) {
                  originalStop.PAXBoarded = updatedStop.PAXBoarded;
                  originalStop.LastUpdated = updatedStop.LastUpdated;
                }
              }
              
              return { 
                ...item, 
                isVisited: true,
                stop: updatedStop,
                PAXBoarded: data.passengerCount || 0,
                LastUpdated: data.timestamp
              };
            }
            return item;
          });
          
          // Find next unvisited waypoint in current loop
          let nextWaypoint = updatedSchedule.find(item => !item.isVisited);
          
          // If no more unvisited waypoints in current loop, start new loop
          if (!nextWaypoint) {
            const newLoopIteration = (updatedSchedule[0]?.loopIndex || 0) + 1;
            
            // Check if we're still within schedule time
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            const scheduleEndTime = updatedSchedule[0]?.runSchedule?.EndTime || (24 * 60); // Default to end of day
            
            if (currentMinutes < scheduleEndTime) {
              // Reset all stops for new loop iteration
              updatedSchedule.forEach((item, index) => {
                item.isVisited = false;
                item.loopIndex = newLoopIteration;
                item.key = `${item.stop.Id}_${newLoopIteration}_${index}`;
              });
              
              nextWaypoint = updatedSchedule[0]; // First stop of new loop
            } else {
              nextWaypoint = null;
            }
          }
          
          // Update the current schedule
          currentDaySchedule = updatedSchedule;
          
          if (nextWaypoint) {
            currentWaypointKey = nextWaypoint.key;
            
            // Update job details with new current waypoint
            const jobs = processLoopedJobData(updatedSchedule, nextWaypoint);
            setJobDetails(jobs);
          } else {
            // No more waypoints - schedule is complete
            console.log('Looped schedule completed - no more waypoints');
            currentWaypointKey = null;
            setJobDetails([]);
          }
        }
      }
      else if (result.command === 'WAYPOINT_UPDATE') {
        if (currentDaySchedule.length > 0) {
          // Find and advance to next waypoint in looped schedule
          const updatedSchedule = currentDaySchedule.map(item => {
            if (item.key === currentWaypointKey) {
              return { ...item, isVisited: true };
            }
            return item;
          });
          
          // Find next unvisited waypoint
          let nextWaypoint = updatedSchedule.find(item => !item.isVisited);
          
          // If no more unvisited waypoints in current loop, start new loop
          if (!nextWaypoint) {
            const newLoopIteration = (updatedSchedule[0]?.loopIndex || 0) + 1;
            nextWaypoint = {
              ...updatedSchedule[0],
              loopIndex: newLoopIteration,
              isVisited: false,
              key: `${updatedSchedule[0].stop.Id}_${newLoopIteration}_0`
            };
            
            // Reset all stops for new loop
            updatedSchedule.forEach(item => {
              item.isVisited = false;
              item.loopIndex = newLoopIteration;
              item.key = `${item.stop.Id}_${newLoopIteration}_${item.stopIndex}`;
            });
          }
          
          currentDaySchedule = updatedSchedule;
          currentWaypointKey = nextWaypoint.key;
          
          // Update job details
          const jobs = processLoopedJobData(updatedSchedule, nextWaypoint);
          setJobDetails(jobs);
        }
      }
    } catch (error) {
      console.error('Error processing looped socket message:', error);
    }
  });

  // Listen for fixed route stop updates to update itinerary status directly
  socket.on('fixedRouteStopUpdate', function (updateData) {
    try {
      // Check if this update is for a looped route action (including basic ARRIVE/DEPART from FIXEDSTOP_ messages)
      if (updateData.action && (updateData.action.startsWith('LOOPED_ROUTE_') || updateData.action === 'ARRIVE' || updateData.action === 'DEPART')) {
  

        // Check if we have a handler function for route updates
        if (handleLoopedRouteData && typeof handleLoopedRouteData === 'function') {
          // Call the handler directly with the update data for socket-based updates
          handleLoopedRouteData(updateData);
        }
      }
    } catch (error) {
      console.error('Error processing fixedRouteStopUpdate:', error);
    }
  });

  // Handle socket reconnection - log for debugging
  socket.on('reconnect', function (attemptNumber) {
    console.log('Looped socket reconnected after', attemptNumber, 'attempts - listeners remain attached');
  });

  isInitialized = true;

  return () => {
    if (socket) {
      socket.off('Command');
      socket.off('fixedRouteStopUpdate');
      socket.off('reconnect');
    }
    isInitialized = false;
  };
};

export { 
  initializeLoopedSocket, 
  processLoopedScheduleData, 
  processLoopedJobData,
  formatMinutesToTime,
  sendDeleteLogMessage
};
